import { useEffect, useRef } from "react";
import type { LostDeviceConfig } from "../scenario/types";
import type { PinScreen } from "../three/PackPinProjector";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";
import { audio } from "../lib/audio";
import { tx } from "../lib/tx";

export function LostDevice({
  config,
  pinScreens = [],
  onChoose,
}: {
  config: LostDeviceConfig;
  pinScreens?: PinScreen[];
  onChoose: (opt: LostDeviceConfig["options"][number]) => void;
}) {
  const spoke = useRef(false);
  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    for (const o of config.options) {
      const line = o.replyLine ?? (o.safe ? config.lenaSafe : config.lenaRisky);
      if (line) void audio.prefetchSpeech(line, { voice: "nova", lang: config.language ?? "en-US" });
    }
    if (spoke.current) return;
    spoke.current = true;
    void audio.speak(config.body ?? "Max's laptop bag is still under the train seat.", {
      voice: config.voiceId ?? "onyx",
      lang: config.language ?? "en-US",
    });
    return () => audio.cancelSpeech();
  }, [config]);

  const bagPin = config.bagHotspot
    ? pinScreens.find((pin) => pin.id === config.bagHotspot?.id)
    : undefined;
  const foundOption = config.options.find((option) => option.hiddenInList);

  const choose = (option: LostDeviceConfig["options"][number]) => {
    const line = option.replyLine ?? (option.safe ? config.lenaSafe : config.lenaRisky);
    if (line) void audio.speak(line, { voice: "nova", lang: config.language ?? "en-US" });
    onChoose(option);
  };

  return (
    <>
      {bagPin?.visible && foundOption && (
        <button
          type="button"
          className="lost-bag-find"
          style={{ left: `${bagPin.xPct}%`, top: `${bagPin.yPct}%` }}
          onClick={() => choose(foundOption)}
        >
          {config.bagHotspot?.spriteUrl && (
            <img className="lost-bag-sprite" src={config.bagHotspot.spriteUrl} alt="" aria-hidden />
          )}
          <span className="lost-bag-caption">
            <strong>{tx("Laptop bag")}</strong>
            <small>{tx("Max got lucky — pick it up")}</small>
          </span>
        </button>
      )}
      <div className="rw-panel lostdevice">
        <div className="rw-kicker">{tx("Missing device")}</div>
        <h2>{config.headline ?? tx("The laptop that wanted freedom")}</h2>
        {config.body && <p className="rw-body">{config.body}</p>}
        <ChoiceList prompt={tx("What should Max do first?")}>
          {config.options.filter((option) => !option.hiddenInList).map((o) => (
            <ChoiceCard
              key={o.id}
              category={o.category}
              showRiskLabel={false}
              reply={o.label}
              onClick={() => choose(o)}
            />
          ))}
        </ChoiceList>
      </div>
    </>
  );
}
